/**
 * DrawingService.ts
 * 
 * 职责：
 * 1. 管理画布上的所有绘图元素(线条、形状)的数据和状态
 * 2. 提供绘图操作的核心方法，如开始绘制、继续绘制和完成绘制
 * 3. 处理形状创建和修改的逻辑，包括矩形、圆形、三角形和直线
 * 4. 管理绘图属性，如画笔颜色和大小
 * 5. 提供元素的选择和碰撞检测功能
 * 6. 负责渲染所有绘图元素到画布上
 * 7. 提供绘图元素的增删改查功能
 * 8. 处理不同绘图模式下的交互逻辑
 */

import { Point, ShapeType, LineData } from '../../types';

export class DrawingService {
  private ctx: CanvasRenderingContext2D | null = null;
  private currentPath: Path2D | null = null;
  private lines: LineData[] = [];
  
  // 为画笔工具和形状工具分别创建独立的颜色和粗细设置
  private penColor: string = '#000000';
  private penSize: number = 2;
  private shapeColor: string = '#000000';
  private shapeSize: number = 2;
  
  private currentShapeStartPoint: Point | null = null;
  private currentShapeEndPoint: Point | null = null;
  private currentShapeType: ShapeType = ShapeType.RECTANGLE;

  constructor() {
    // 设置默认形状类型为矩形
    this.currentShapeType = ShapeType.RECTANGLE;
  }

  // 设置画布上下文
  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  // 获取所有线条
  public getLines(): LineData[] {
    return this.lines;
  }

  // 设置线条数据
  public setLines(lines: LineData[]): void {
    this.lines = lines;
  }

  // 获取选定的画笔颜色
  public getSelectedPenColor(): string {
    return this.penColor;
  }

  // 设置画笔颜色
  public setSelectedPenColor(color: string): void {
    this.penColor = color;
  }

  // 获取选定的画笔粗细
  public getSelectedPenSize(): number {
    return this.penSize;
  }

  // 设置画笔粗细
  public setSelectedPenSize(size: number): void {
    this.penSize = size;
  }
  
  // 获取选定的形状颜色
  public getShapeColor(): string {
    return this.shapeColor;
  }

  // 设置形状颜色
  public setShapeColor(color: string): void {
    this.shapeColor = color;
  }

  // 获取选定的形状线条粗细
  public getShapeSize(): number {
    return this.shapeSize;
  }

  // 设置形状线条粗细
  public setShapeSize(size: number): void {
    this.shapeSize = size;
  }

  // 开始绘制线条
  public startDrawing(point: Point): void {
    if (!this.ctx) return;
    
    this.currentPath = new Path2D();
    this.currentPath.moveTo(point.x, point.y);
    
    const newLine: LineData = {
      path: this.currentPath,
      color: this.penColor,
      size: this.penSize,
      points: [{ x: point.x, y: point.y }],
      lineCap: 'round',
      lineJoin: 'round',
      isConvertedShape: false,
      originalShapeType: undefined,
      shapeStartPoint: undefined,
      shapeEndPoint: undefined
    };
    
    this.lines.push(newLine);
    
    // 如果是单击（只有一个点），绘制一个小圆点，使之可见
    if (this.ctx) {
      this.ctx.fillStyle = this.penColor;
      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, this.penSize / 2, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  // 继续绘制线条
  public continueDrawing(point: Point): void {
    if (!this.ctx || !this.currentPath || this.lines.length === 0) return;
    
    const currentLine = this.lines[this.lines.length - 1];
    this.currentPath.lineTo(point.x, point.y);
    
    // 更新线条数据
    currentLine.points.push({ x: point.x, y: point.y });
    
    // 绘制当前线条
    this.ctx.strokeStyle = currentLine.color;
    this.ctx.lineWidth = currentLine.size;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.stroke(this.currentPath);
  }

  // 取消当前绘制的线条
  public cancelDrawing(): void {
    // 如果有正在绘制的线条，移除它
    if (this.lines.length > 0) {
      this.lines.pop();
    }
    
    // 发射工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }

  // 完成绘制
  public finishDrawing(): void {
    this.currentPath = null;
    
    // 发射工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }

  // 开始绘制形状
  public startShape(point: Point): void {
    this.currentShapeStartPoint = { ...point };
    this.currentShapeEndPoint = { ...point };
  }

  // 更新形状
  public updateShape(point: Point): void {
    if (!this.currentShapeStartPoint) {
      return;
    }
    
    this.currentShapeEndPoint = { ...point };
  }

  // 取消形状创建操作
  public cancelShapeCreation(): void {
    // 重置所有形状绘制相关状态
    if (this.currentShapeStartPoint !== null || this.currentShapeEndPoint !== null) {
      this.currentShapeStartPoint = null;
      this.currentShapeEndPoint = null;
    }
    
    // 发射工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }

  // 完成形状绘制
  public finishShape(): void {
    if (!this.currentShapeStartPoint || !this.currentShapeEndPoint) return;
    
    // 确保起始点和结束点不同
    if (
      this.currentShapeStartPoint.x === this.currentShapeEndPoint.x && 
      this.currentShapeStartPoint.y === this.currentShapeEndPoint.y
    ) {
      this.currentShapeStartPoint = null;
      this.currentShapeEndPoint = null;
      return;
    }
    
    // 创建形状路径
    const shapePath = new Path2D();
    this.drawShapePath(
      shapePath, 
      this.currentShapeType, 
      this.currentShapeStartPoint, 
      this.currentShapeEndPoint
    );
    
    // 生成形状轮廓点集合
    const shapePoints = this.generateShapePoints(
      this.currentShapeType,
      this.currentShapeStartPoint,
      this.currentShapeEndPoint
    );
    
    // 核心设计理念：形状仅仅在创建阶段存在
    // 一旦用户完成绘制，形状立即被转换为普通线条
    // 不保留任何形状的原始信息（类型、起止点等）
    // 这样可以确保后续的所有操作（选择、移动等）都基于统一的线条模型
    const newLine: LineData = {
      path: shapePath,
      color: this.shapeColor,
      size: this.shapeSize,
      lineCap: 'round',
      lineJoin: 'round',
      points: shapePoints,
      isConvertedShape: false,
      originalShapeType: undefined,
      shapeStartPoint: undefined,
      shapeEndPoint: undefined
    };
    
    this.lines.push(newLine);
    
    // 重置当前形状状态
    this.currentShapeStartPoint = null;
    this.currentShapeEndPoint = null;
    
    // 发射工具操作完成事件
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
  }

  // 绘制形状路径
  public drawShapePath(
    path: Path2D, 
    type: ShapeType, 
    startPoint: Point, 
    endPoint: Point
  ): void {
    switch (type) {
      case ShapeType.RECTANGLE:
        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);
        path.rect(x, y, width, height);
        break;
        
      case ShapeType.ROUNDED_RECTANGLE:
        const rx = Math.min(startPoint.x, endPoint.x);
        const ry = Math.min(startPoint.y, endPoint.y);
        const rWidth = Math.abs(endPoint.x - startPoint.x);
        const rHeight = Math.abs(endPoint.y - startPoint.y);
        const cornerRadius = Math.min(rWidth, rHeight) * 0.2; // 圆角半径为宽高最小值的20%
        
        // 绘制圆角矩形路径
        path.moveTo(rx + cornerRadius, ry); // 从左上角的圆角处开始
        path.lineTo(rx + rWidth - cornerRadius, ry); // 顶部线条
        path.arcTo(rx + rWidth, ry, rx + rWidth, ry + cornerRadius, cornerRadius); // 右上角圆角
        path.lineTo(rx + rWidth, ry + rHeight - cornerRadius); // 右侧线条
        path.arcTo(rx + rWidth, ry + rHeight, rx + rWidth - cornerRadius, ry + rHeight, cornerRadius); // 右下角圆角
        path.lineTo(rx + cornerRadius, ry + rHeight); // 底部线条
        path.arcTo(rx, ry + rHeight, rx, ry + rHeight - cornerRadius, cornerRadius); // 左下角圆角
        path.lineTo(rx, ry + cornerRadius); // 左侧线条
        path.arcTo(rx, ry, rx + cornerRadius, ry, cornerRadius); // 左上角圆角
        path.closePath();
        break;
        
      case ShapeType.CIRCLE:
        const centerX = (startPoint.x + endPoint.x) / 2;
        const centerY = (startPoint.y + endPoint.y) / 2;
        const radius = Math.sqrt(
          Math.pow(endPoint.x - startPoint.x, 2) + 
          Math.pow(endPoint.y - startPoint.y, 2)
        ) / 2;
        path.arc(centerX, centerY, radius, 0, Math.PI * 2);
        break;
        
      case ShapeType.TRIANGLE:
        path.moveTo(startPoint.x, endPoint.y);
        path.lineTo((startPoint.x + endPoint.x) / 2, startPoint.y);
        path.lineTo(endPoint.x, endPoint.y);
        path.closePath();
        break;
        
      case ShapeType.LINE:
        path.moveTo(startPoint.x, startPoint.y);
        path.lineTo(endPoint.x, endPoint.y);
        break;
    }
  }

  // 删除指定索引的线条
  public deleteLines(indices: number[]): void {
    this.lines = this.lines.filter((_, index) => !indices.includes(index));
  }

  // 绘制所有元素
  public drawAllElements(): void {
    if (!this.ctx) return;
    
    // 绘制所有线条（包括转换后的形状）
    this.lines.forEach(line => {
      if (this.ctx) {
        this.ctx.strokeStyle = line.color;
        this.ctx.lineWidth = line.size;
        
        // 应用保存的线帽和线条连接样式，如果存在
        this.ctx.lineCap = line.lineCap || 'butt';  // 默认是 'butt'
        this.ctx.lineJoin = line.lineJoin || 'miter'; // 默认是 'miter'
        
        this.ctx.stroke(line.path);
        
        // 如果线条只有一个点，绘制一个圆点使之可见
        if (line.points.length === 1) {
          this.ctx.fillStyle = line.color;
          this.ctx.beginPath();
          this.ctx.arc(line.points[0].x, line.points[0].y, line.size / 2, 0, Math.PI * 2);
          this.ctx.fill();
        }
      }
    });
    
    // 如果正在绘制形状，绘制当前形状预览
    if (this.currentShapeStartPoint && this.currentShapeEndPoint) {
      this.drawCurrentShapePreview();
    }
  }

  // 绘制当前形状预览
  private drawCurrentShapePreview(): void {
    if (!this.ctx || !this.currentShapeStartPoint || !this.currentShapeEndPoint) {
      return;
    }
    
    // 保存当前上下文状态
    this.ctx.save();
    
    // 设置样式，使用形状专用的颜色和粗细
    this.ctx.strokeStyle = this.shapeColor;
    this.ctx.lineWidth = this.shapeSize;
    // 应用圆形线帽和线条连接样式
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    
    // 创建临时路径
    const tempPath = new Path2D();
    this.drawShapePath(
      tempPath, 
      this.currentShapeType, 
      this.currentShapeStartPoint, 
      this.currentShapeEndPoint
    );
    
    // 绘制路径
    this.ctx.stroke(tempPath);
    
    // 恢复上下文状态
    this.ctx.restore();
  }
  
  // 设置当前形状类型
  public setShapeType(type: ShapeType): void {
    this.currentShapeType = type;
  }
  
  // 获取当前形状类型
  public getShapeType(): ShapeType {
    return this.currentShapeType;
  }

  // 检测点击是否与某个线条重合
  public hitTest(point: Point): number {
    if (!this.ctx) return -1;
    
    // 保存当前上下文状态
    this.ctx.save();
    
    // 修改上下文属性
    this.ctx.lineWidth = 10; // 增加线宽以便于选取
    
    // 从前往后检查线条，保持和渲染一致的顺序
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines[i];
      
      // 如果线条没有点，则跳过
      if (!line.points.length) continue;
      
      // 只使用线段距离检测（更精确且统一）
      if (line.points.length > 1) {
        // 遍历线条的所有线段
        for (let j = 0; j < line.points.length - 1; j++) {
          const p1 = line.points[j];
          const p2 = line.points[j + 1];
          
          // 计算点到线段的距离
          const distance = this.pointToLineDistance(point, p1, p2);
          
          // 如果距离小于线宽+5像素，则认为点击在线条上
          if (distance <= line.size + 5) {
            this.ctx.restore();
            return i;
          }
        }
      }
      // 对于很短的线条（单击形成的点），需要特殊处理
      else if (line.points.length === 1) {
        // 计算点到线条起点的距离
        const distance = Math.sqrt(
          Math.pow(point.x - line.points[0].x, 2) + 
          Math.pow(point.y - line.points[0].y, 2)
        );
        // 如果距离小于一定范围（线宽+3），则认为点击在线条上
        if (distance <= line.size + 3) {
          this.ctx.restore();
          return i;
        }
      }
    }
    
    // 恢复上下文状态
    this.ctx.restore();
    
    return -1; // 没有找到匹配的线条
  }
  
  // 计算点到线段的最短距离
  private pointToLineDistance(point: Point, lineStart: Point, lineEnd: Point): number {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    
    // 线段长度的平方
    const lengthSquared = dx * dx + dy * dy;
    
    // 如果线段实际上是一个点
    if (lengthSquared === 0) {
      return Math.sqrt(
        Math.pow(point.x - lineStart.x, 2) + 
        Math.pow(point.y - lineStart.y, 2)
      );
    }
    
    // 计算投影点的参数 t (0 <= t <= 1)
    const t = Math.max(0, Math.min(1, 
      ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared
    ));
    
    // 计算投影点坐标
    const projectionX = lineStart.x + t * dx;
    const projectionY = lineStart.y + t * dy;
    
    // 计算点到投影点的距离
    return Math.sqrt(
      Math.pow(point.x - projectionX, 2) + 
      Math.pow(point.y - projectionY, 2)
    );
  }

  // 生成形状轮廓点数组的方法
  private generateShapePoints(type: ShapeType, startPoint: Point, endPoint: Point): Point[] {
    const points: Point[] = [];
    
    switch (type) {
      case ShapeType.RECTANGLE: {
        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);
        
        // 矩形四个顶点加第一个点以闭合
        points.push({ x, y });                    // 左上
        points.push({ x: x + width, y });         // 右上
        points.push({ x: x + width, y: y + height }); // 右下
        points.push({ x, y: y + height });        // 左下
        points.push({ x, y });                    // 闭合回左上
        break;
      }
      
      case ShapeType.ROUNDED_RECTANGLE: {
        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);
        const cornerRadius = Math.min(width, height) * 0.2; // 圆角半径为宽高最小值的20%
        
        // 生成圆角矩形的轮廓点
        // 为了平滑的圆角效果，每个圆角使用8个点来近似
        const arcPointCount = 8;
        
        // 左上角到右上角（顶部）
        points.push({ x: x + cornerRadius, y }); // 左上角圆角后的第一个点
        points.push({ x: x + width - cornerRadius, y }); // 右上角圆角前的点
        
        // 右上角的圆弧
        for (let i = 1; i <= arcPointCount; i++) {
          const angle = Math.PI * 1.5 + (Math.PI / 2) * (i / arcPointCount);
          points.push({
            x: x + width - cornerRadius + cornerRadius * Math.cos(angle),
            y: y + cornerRadius + cornerRadius * Math.sin(angle)
          });
        }
        
        // 右上角到右下角（右侧）
        points.push({ x: x + width, y: y + cornerRadius }); // 右上角圆角后的点
        points.push({ x: x + width, y: y + height - cornerRadius }); // 右下角圆角前的点
        
        // 右下角的圆弧
        for (let i = 1; i <= arcPointCount; i++) {
          const angle = 0 + (Math.PI / 2) * (i / arcPointCount);
          points.push({
            x: x + width - cornerRadius + cornerRadius * Math.cos(angle),
            y: y + height - cornerRadius + cornerRadius * Math.sin(angle)
          });
        }
        
        // 右下角到左下角（底部）
        points.push({ x: x + width - cornerRadius, y: y + height }); // 右下角圆角后的点
        points.push({ x: x + cornerRadius, y: y + height }); // 左下角圆角前的点
        
        // 左下角的圆弧
        for (let i = 1; i <= arcPointCount; i++) {
          const angle = Math.PI / 2 + (Math.PI / 2) * (i / arcPointCount);
          points.push({
            x: x + cornerRadius + cornerRadius * Math.cos(angle),
            y: y + height - cornerRadius + cornerRadius * Math.sin(angle)
          });
        }
        
        // 左下角到左上角（左侧）
        points.push({ x, y: y + height - cornerRadius }); // 左下角圆角后的点
        points.push({ x, y: y + cornerRadius }); // 左上角圆角前的点
        
        // 左上角的圆弧
        for (let i = 1; i <= arcPointCount; i++) {
          const angle = Math.PI + (Math.PI / 2) * (i / arcPointCount);
          points.push({
            x: x + cornerRadius + cornerRadius * Math.cos(angle),
            y: y + cornerRadius + cornerRadius * Math.sin(angle)
          });
        }
        
        // 闭合轮廓
        points.push({ x: x + cornerRadius, y }); // 回到起点
        break;
      }
      
      case ShapeType.CIRCLE: {
        const centerX = (startPoint.x + endPoint.x) / 2;
        const centerY = (startPoint.y + endPoint.y) / 2;
        const radius = Math.sqrt(
          Math.pow(endPoint.x - startPoint.x, 2) + 
          Math.pow(endPoint.y - startPoint.y, 2)
        ) / 2;
        
        // 圆形生成更密集的点（每2.5度生成一个点）
        const angleStep = Math.PI / 72; // 2.5度一个点
        for (let angle = 0; angle < Math.PI * 2; angle += angleStep) {
          points.push({
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
          });
        }
        
        // 闭合圆形
        points.push({
          x: centerX + radius,
          y: centerY
        });
        break;
      }
      
      case ShapeType.TRIANGLE: {
        // 三角形三个顶点加第一个点以闭合
        points.push({ x: startPoint.x, y: endPoint.y });
        points.push({ x: (startPoint.x + endPoint.x) / 2, y: startPoint.y });
        points.push({ x: endPoint.x, y: endPoint.y });
        points.push({ x: startPoint.x, y: endPoint.y }); // 闭合
        break;
      }
      
      case ShapeType.LINE: {
        // 直线只有两个端点
        points.push({ x: startPoint.x, y: startPoint.y });
        points.push({ x: endPoint.x, y: endPoint.y });
        break;
      }
    }
    
    return points;
  }
  
  // 公开测试用的生成形状点方法
  public testGenerateShapePoints(type: ShapeType, startPoint: Point, endPoint: Point): Point[] {
    return this.generateShapePoints(type, startPoint, endPoint);
  }

  // 更新指定索引线条的点集合
  public updateLinePoints(index: number, newPoints: Point[]): void {
    if (index < 0 || index >= this.lines.length) return;
    
    const line = this.lines[index];
    line.points = newPoints;
    
    // 重新生成路径
    const newPath = new Path2D();
    if (newPoints.length > 0) {
      newPath.moveTo(newPoints[0].x, newPoints[0].y);
      for (let i = 1; i < newPoints.length; i++) {
        newPath.lineTo(newPoints[i].x, newPoints[i].y);
      }
    }
    
    // 更新线条的路径
    line.path = newPath;
  }
}